import { resolveMicDenialTarget } from './micPermission';

/**
 * Чем кончился запрос доступа к микрофону, сделанный ДО входа в разговор.
 *
 * <p>`pending` — вопрос браузера ещё на экране. `unavailable` — не «запрещено»: микрофона может
 * не быть вовсе или его держит другое приложение. Совет «разрешите доступ» в этих случаях был бы
 * неправдой, поэтому исход отделён от честного отказа.
 */
export type MicAccess = 'pending' | 'granted' | 'denied' | 'unavailable';

/** Ответ на вопрос — всё, кроме «ещё спрашиваем». */
export type MicAnswer = Exclude<MicAccess, 'pending'>;

/**
 * Сколько исходящий звонок ждёт ответа на системный диалог, прежде чем уйти с открытым вопросом.
 *
 * <p>Диалог живёт на экране сколько угодно, а обещание `getUserMedia` не отменяется ничем: без
 * срока звонок у человека, который отошёл с открытым вопросом браузера, не ушёл бы никогда.
 * Срок ограничивает только ожидание звонка — сам вопрос остаётся, и ответ, данный позже,
 * всё равно доезжает до экрана: прогон на телефоне 16.09 показал, что отказ на 25-й секунде
 * иначе терялся, и человек оставался без подсказки ровно в том случае, ради которого она есть.
 */
export const MIC_PROMPT_WAIT_MS = 20_000;

/**
 * Стоит ли спрашивать микрофон заранее — только в браузере на Android.
 *
 * <p>Прод за неделю 09–16.09.2026: `android-chrome` — 9 отказов микрофона на 14 участий в
 * звонках (64 %), `android-app` — ни одного на 10. Дело не в людях, а в моменте вопроса: в
 * приложении разрешение спрашивают системно и один раз, а в браузере диалог всплывает уже
 * ВНУТРИ разговора, вне пользовательского жеста, поверх звонящего экрана — и отбивается на
 * автомате. Отказ Chrome помнит за сайтом, так что одно случайное нажатие ломает все следующие
 * звонки: 14.09 человек пять раз подряд звонил одному и тому же собеседнику, все пять раз
 * соединился и все пять раз остался неслышимым, один такой «разговор» длился 16 минут.
 *
 * <p>На остальных средах не трогаем: в приложении свой поток разрешений, на десктопе отказов
 * 9 %, а Safari требует жеста для звука, и лишний захват там только мешает.
 */
export const shouldPrimeMicAccess = (): boolean =>
  resolveMicDenialTarget() === 'android-browser' &&
  typeof navigator !== 'undefined' &&
  typeof navigator.mediaDevices?.getUserMedia === 'function';

/**
 * Запросить микрофон до входа в комнату и сразу отпустить его.
 *
 * <p>Вызывать ПЕРВЫМ делом внутри обработчика нажатия — до любых `await`: право показать диалог
 * даётся жестом, и после первого же круга к серверу его уже нет. Захваченный поток тут же
 * останавливается: он нужен ровно на то, чтобы браузер задал свой вопрос, а говорить человек
 * будет через дорожку, которую заведёт сама комната.
 *
 * <p>Обещание решается только ответом человека (или отказом устройства) и срока не имеет:
 * сколько ждать, решает вызывающий, см. {@link MIC_PROMPT_WAIT_MS}.
 */
export const primeMicAccess = (): Promise<MicAnswer> => {
  let capture: Promise<MediaStream>;
  try {
    capture = navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    // Синхронный бросок бывает на старых webview и на небезопасном источнике.
    return Promise.resolve<MicAnswer>('unavailable');
  }

  return capture.then(
    (stream): MicAnswer => {
      stream.getTracks().forEach((track) => track.stop());
      return 'granted';
    },
    (error: unknown): MicAnswer => {
      // Отказ человека и отсутствие микрофона приходят одним и тем же способом, а лечатся
      // по-разному: подсказка «разрешите доступ» тому, у кого микрофона нет, — вредный совет.
      const name = error instanceof Error ? error.name : '';
      return name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable';
    }
  );
};
